import { useState, type ComponentProps } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@kss/ui';

type InputProps = ComponentProps<typeof Input>;

/**
 * Password input with a built-in show/hide eye toggle.
 *
 * Wraps `<Input>` and overlays the toggle button absolutely on the right
 * edge of the field. The toggle remains keyboard-accessible (tabindex 0)
 * and announces its state via `aria-label`.
 */
export function PasswordField(props: Omit<InputProps, 'type'>) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        type={visible ? 'text' : 'password'}
        // Reserve space so the eye icon doesn't overlap typed text.
        className={[props.className ?? '', 'pr-10'].filter(Boolean).join(' ')}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        // The label adds 1.25rem of height above the input — offset the icon
        // by that amount when a label is present.
        className={[
          'absolute right-2 inline-flex h-7 w-7 items-center justify-center rounded text-content-muted hover:text-content-primary',
          props.label ? 'top-[28px]' : 'top-1/2 -translate-y-1/2',
        ].join(' ')}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
